import { Hidden, Theme, Typography, makeStyles } from '@material-ui/core';
import { ICommunity, ICourse } from 'interface/course';
import { useEffect, useRef, useState } from 'react';

import ArrowDownIcon from '@eduzz/houston-icons/ChevronDown';
import Avatar from 'components/Shared/Avatar';
import { IUser } from 'interface/user';
import Icon from '@material-ui/core/Icon';
import { Link } from 'react-router-dom';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { V3_URL } from 'settings';
import { connect } from 'react-redux';
import courseService from 'services/course';
import { getCdnUrl } from 'utils';
import { logout } from 'actionCreators/auth';
import { map } from 'rxjs';
import palette from 'assets/themes/Houston/palette';
import profileService from 'services/profile';
import { useAppSelector } from 'hooks/redux';

interface IProps {
  user: IUser;
  course?: ICourse;
  logout: typeof logout;
  toggleModalTurnProducer: () => void;
  subdomain: string;
  courseHash: string;
}

const useStyles = makeStyles<Theme, { color: string }>((theme) => ({
  menuContainer: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    '& .houston-icon, p': {
      color: (props) => props.color ?? theme.palette.common.white,
    },
  },
  menu: {
    background: palette.dark[600],
    overflow: 'visible',
    '&:after': {
      content: "''",
      position: 'absolute',
      backgroundColor: 'transparent',
      borderLeft: '10px solid transparent',
      borderRight: '10px solid transparent',
      borderBottom: `10px solid ${theme.palette.dark[600]}`,
      top: -10,
      right: 20,
    },
    '& li, span': {
      fontSize: 14,
      color: theme.palette.common.white,
    },
  },
  hideOnDesktop: {
    '@media(min-width: 770px)': {
      display: 'none',
    },
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
  },
  name: {
    maxWidth: 300,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    margin: theme.spacing(0, 1),
    fontWeight: 600,
    fontSize: 14,
    color: theme.palette.common.white,
  },
  avatar: {
    width: 40,
    height: 40,
    objectFit: 'cover',
  },
}));

const UserMenu = (props: IProps) => {
  const [menuIsOpened, setMenuisOpen] = useState(false);
  const [avatar, setAvatar] = useState('');
  const [communityUrl, setCommunityUrl] = useState<ICommunity>({} as ICommunity);
  const menuRef = useRef<HTMLDivElement>(null);
  const { header_link_color } = useAppSelector((store) => store.customizations);
  const classes = useStyles({ color: header_link_color });

  const getFirstName = () => {
    if (!props.user.name) return '';
    if (props.user.name.includes(' ')) {
      return props.user.name.split(' ')[0];
    }
    return props.user.name;
  };

  useEffect(() => {
    const sub1$ = profileService
      .getProfilePicture()
      .pipe(
        map((avatar) => {
          if (avatar) return avatar;
          return props.user.avatar;
        })
      )
      .subscribe((next) => setAvatar(next));
    return () => {
      sub1$.unsubscribe();
    };
  }, [props.user]);

  const getProfilePhoto = () => {
    return getCdnUrl(avatar);
  };

  const toggleMenu = () => {
    setMenuisOpen((isOpen) => !isOpen);
  };

  const openAsProducer = () => {
    window.location.href = V3_URL;
  };

  const logout = () => {
    props.logout(`/${props.courseHash || ''}`);
  };

  const hrefToSecretaryPage = (): void => {
    const { course } = props;
    window.open(course.secretary_url);
  };

  const hrefToCommunityPage = (): void => {
    const { course } = props;
    courseService.getCommunityUrl(course.hash).then((data) => setCommunityUrl(data));
    window.open(communityUrl.url);
  };

  const hrefToDepoimentusPage = (): void => {
    const { course } = props;
    window.open(course.depoimentu_url);
  };

  return (
    <div className={classes.menuContainer}>
      <div id='header-user-icon' className={classes.container} ref={menuRef} onClick={toggleMenu}>
        <Avatar className={classes.avatar} width={32} alt={props.user.name} src={getProfilePhoto()} />
        <Hidden smDown>
          <Typography className={classes.name}>{getFirstName()}</Typography>
          <ArrowDownIcon id='arrow-down' size={20} />
        </Hidden>
      </div>
      <Menu
        id='header-user-menu'
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        classes={{ paper: classes.menu }}
        getContentAnchorEl={null}
        anchorEl={menuRef?.current}
        open={Boolean(menuIsOpened)}
        onClose={toggleMenu}
      >
        {!!props.user.type && (
          <div id='header-link-view-producer' className='producer_link' onClick={openAsProducer}>
            <MenuItem>
              <Icon style={{ fontSize: 18, marginRight: 10 }}>open_in_new</Icon> Visualizar como produtor
            </MenuItem>
          </div>
        )}
        <Link id='header-link-profile' to='/perfil' style={{ color: 'inherit' }}>
          <MenuItem>
            <Icon style={{ fontSize: 18, marginRight: 10 }}>home</Icon> Perfil
          </MenuItem>
        </Link>
        <Link
          id='header-link-certificates'
          className={classes.hideOnDesktop}
          to='/certificados'
          style={{ color: 'inherit' }}
        >
          <MenuItem>
            <Icon style={{ fontSize: 18, marginRight: 10 }}>file_copy</Icon> Meus Certificados
          </MenuItem>
        </Link>
        <Link id='link_certificates' className={classes.hideOnDesktop} to='/aulas/salvas' style={{ color: 'inherit' }}>
          <MenuItem>
            <Icon style={{ fontSize: 18, marginRight: 10 }}>save</Icon> Aulas Salvas
          </MenuItem>
        </Link>
        {!props.user.type && (
          <div
            id='header-link-turn-producer'
            className={!props.subdomain && classes.hideOnDesktop}
            onClick={props.toggleModalTurnProducer}
          >
            <MenuItem>
              <Icon style={{ fontSize: 18, marginRight: 10 }}>add</Icon> Criar curso
            </MenuItem>
          </div>
        )}
        {props.courseHash && props.course && props.course.secretary_status && (
          <div
            id='header-link-secretary'
            className={props.course.secretary_status && classes.hideOnDesktop}
            onClick={hrefToSecretaryPage}
          >
            <MenuItem>
              <Icon style={{ fontSize: 18, marginRight: 10 }}>open_in_new</Icon> Secretaria
            </MenuItem>
          </div>
        )}
        {props.courseHash && props.course && props.course.have_active_community && (
          <div
            id='header-link-community'
            className={props.course.secretary_status && classes.hideOnDesktop}
            onClick={hrefToCommunityPage}
          >
            <MenuItem>
              <Icon style={{ fontSize: 18, marginRight: 10 }}>open_in_new</Icon> Comunidade
            </MenuItem>
          </div>
        )}
        {props.courseHash && props.course && props.course.depoimentu_url && (
          <div id='header-link-community' className={classes.hideOnDesktop} onClick={hrefToDepoimentusPage}>
            <MenuItem>
              <Icon style={{ fontSize: 18, marginRight: 10 }}>open_in_new</Icon> Depoimentos
            </MenuItem>
          </div>
        )}
        <MenuItem id='header-link-logout' onClick={logout}>
          <Icon style={{ fontSize: 18, marginRight: 10, color: 'inherit' }}>power_settings_new</Icon>
          <div style={{ color: 'inherit' }}>Sair</div>
        </MenuItem>
      </Menu>
    </div>
  );
};

const mapStateToProps = (state: any, ownProps: any) => ({
  ...ownProps,
  user: state.user,
  course: state.course,
});

export default connect(mapStateToProps, { logout })(UserMenu);
